/**
 * ฟอร์มหนังสือ HR — ใช้ทั้งตอนสร้างใหม่ (/hr/letters/new) และแก้ร่าง (/hr/letters/:id/edit)
 *
 * ⭐ **preview-driven**: เลือกพนักงาน → ยิง preview → ป้ายหน่วยเงิน ช่องที่โผล่ และค่าที่จะขึ้น
 *    บนกระดาษ มาจาก server ทั้งหมด จอไม่เดาเอง
 *    (บั๊กเดิม: จอติดป้าย "บาท/เดือน" ตายตัว พนักงานรายวันกรอก 15,000 แล้วเอกสารรับรอง
 *     15,000 บาท/วัน · และหนังสือรับรองเงินเดือนของอดีตพนักงานออกไม่ได้เลยเพราะฟอร์มไม่มีช่อง)
 *
 * ⭐ ช่องของทะเบียน (ชื่อ ตำแหน่ง วันเริ่มงาน สถานะ) เป็น **อ่านอย่างเดียว** พร้อมทางไปแก้ทะเบียน
 *    ลูกค้าเป็น SME ไม่มี HR มืออาชีพ — ทางเดียวดีกว่าสองทางที่ขัดกันเอง
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../lib/store';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { FileText, ArrowLeft, AlertTriangle, ExternalLink } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Loading } from '../../components/layout/Loading';
import { LoadError } from '../../components/layout/LoadError';
import { useToast } from '../../components/ui/toast';
import {
  useEmployees,
  useLetterPreview,
  useCreateLetter,
  useLetter,
  usePatchLetter,
  useMyCapabilities,
  WAGE_UNIT_LABEL,
  WARNING_CATEGORY_LABEL,
  WARNING_LEVEL_LABEL,
  type LetterType,
  type LetterRegistry,
  type LetterComputed,
  type LetterMissing,
  type WarningInput,
} from '../../hooks';

type LetterWarningLevel = NonNullable<WarningInput['level']>;
type LetterWarningCategory = NonNullable<WarningInput['category']>;

interface EmpOption {
  emp_code: string;
  first_name?: string | null;
  last_name?: string | null;
  position?: string | null;
  nickname?: string | null;
  department?: string | null;
  status?: string | null;
}

function empMatches(e: EmpOption, q: string): boolean {
  return (
    [e.emp_code, e.first_name, e.last_name, e.nickname, e.position, e.department].some((v) =>
      (v ?? '').toLowerCase().includes(q),
    ) || `${e.first_name ?? ''} ${e.last_name ?? ''}`.toLowerCase().includes(q)
  );
}

const TYPES: { value: LetterType; label: string; hint: string }[] = [
  {
    value: 'employment_cert',
    label: 'หนังสือรับรองการทำงาน',
    hint: 'รับรองว่าเป็นพนักงาน ตำแหน่งอะไร ตั้งแต่เมื่อไหร่ — ไม่มีค่าจ้างในเอกสาร',
  },
  {
    value: 'salary_cert',
    label: 'หนังสือรับรองเงินเดือน',
    hint: 'รับรองค่าจ้าง มักใช้ยื่นกู้/ทำบัตรเครดิต — ระบบดึงฐานค่าจ้างจากทะเบียนมาให้',
  },
  {
    value: 'warning',
    label: 'หนังสือเตือน',
    hint: 'บันทึกการกระทำผิดและคำเตือนเป็นลายลักษณ์อักษร — มีผล 1 ปีนับแต่วันกระทำผิด (ม.119(4))',
  },
];

const lbl = 'block text-sm font-medium text-gray-700 mb-1';
const inp =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-brand-400';

function baht(n: number | undefined): string {
  return typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';
}

function thaiDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('th-TH', {
        timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: 'numeric',
      });
}

/** สรุปสิ่งที่จะขึ้นบนกระดาษ — ให้ HR เห็นก่อนกดบันทึก ไม่ใช่ไปเห็นตอนเปิด PDF */
function RegistryPanel({
  registry,
  computed,
  missing,
  letterType,
  overridePreview,
  isOwner,
}: {
  registry: LetterRegistry;
  computed: LetterComputed;
  missing: LetterMissing[];
  letterType: LetterType;
  /** ข้อความยอดที่ HR กำลังพิมพ์ — undefined เมื่อไม่ได้ระบุเอง */
  overridePreview?: string;
  /** เจ้าของเข้าได้เสมอ ส่วน HR ต้องมี capability edit_company */
  isOwner: boolean;
}) {
  const { data: caps = [], isLoading: capsLoading } = useMyCapabilities();
  const needRegistryFix = missing.filter((m) => m.fixWhere === 'registry');
  // 🚨 ชื่อบริษัท/ผู้ลงนามอยู่หน้า "ตั้งค่าบริษัท" ไม่ได้อยู่ในทะเบียนพนักงาน
  // เดิมบอกให้ไปแก้ที่ทะเบียนแล้วผู้ใช้เปิดไปหาช่องไม่เจอ = ทำตามคำแนะนำแล้วยังตัน
  const needCompanyFix = missing.filter((m) => m.fixWhere === 'company');
  const needFormFix = missing.filter((m) => m.fixWhere === 'form');
  const canEditCompany = isOwner || caps.includes('edit_company');
  const row = (k: string, v: string) => (
    <div className="flex gap-2 py-1 border-b border-gray-100 last:border-0">
      <div className="w-40 shrink-0 text-gray-500">{k}</div>
      <div className="flex-1 font-medium">{v || '—'}</div>
    </div>
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold">ข้อมูลจากทะเบียนพนักงาน</div>
        <Link
          to="/hr/people#all"
          className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline"
        >
          แก้ที่ทะเบียน <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        ช่องเหล่านี้แก้ในหนังสือไม่ได้ — ถ้าไม่ถูกต้อง ให้แก้ที่ทะเบียนพนักงานแล้วกลับมากด “ดึงข้อมูลใหม่”
      </p>

      {/* HR ที่มี edit_company เข้าแก้เองได้ ส่วนคนที่ไม่มีสิทธิ์ต้องเห็นทางขอสิทธิ์ที่ชัดเจน */}
      {needCompanyFix.length > 0 && (
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
          ยังตั้งข้อมูลบริษัทไม่ครบ: {needCompanyFix.map((m) => m.label).join(' · ')}{' '}
          {canEditCompany ? (
            <Link to="/owner/settings#company" className="inline-flex items-center gap-1 underline font-medium">
              ไปตั้งค่าบริษัท <ExternalLink className="w-3 h-3" />
            </Link>
          ) : (
            <span className="block mt-1">
              ช่องนี้อยู่ในหน้าตั้งค่าบริษัท — กรุณาแจ้งเจ้าของกิจการให้กรอก หรือขอสิทธิ์
              {' '}'ตั้งค่าบริษัท (โปรไฟล์)' ที่เมนู ตั้งค่าสิทธิ์ HR แล้วกลับมากด “ดึงข้อมูลใหม่”
            </span>
          )}
        </div>
      )}

      {letterType === 'warning' && needFormFix.length > 0 && (
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
          <span className="font-medium">ยังกรอกไม่ครบ:</span>{' '}
          {needFormFix.map((m) => m.label).join(' · ')}
        </div>
      )}

      {row('ชื่อ-สกุล', registry.empNameTh)}
      {row('ตำแหน่ง', registry.position ?? '')}
      {row('ฝ่าย/แผนก', registry.department ?? '')}
      {row('วันเริ่มงาน', registry.startDate ?? '')}
      {row(
        'สถานะ',
        computed.employmentStatement === 'former_employee'
          ? `พ้นสภาพแล้ว${registry.endDate ? ` (${registry.endDate})` : ''}`
          : 'ยังเป็นพนักงาน',
      )}
      {row('อายุงาน', computed.tenureText)}
      {letterType === 'warning' && computed.warningValidUntil &&
        row('มีผลถึง', thaiDate(computed.warningValidUntil))}
      {letterType === 'salary_cert' &&
        row(
          'ค่าจ้างตามทะเบียน',
          registry.wage
            ? `${baht(registry.wage.amount)} บาท/${WAGE_UNIT_LABEL[registry.wage.unit]}`
            : '',
        )}
      {/* ยอดที่ผู้ใช้กำลังพิมพ์ — แสดงแยกบรรทัด จอไม่คำนวณเอง แค่สะท้อนสิ่งที่กรอก
          (เดิมโชว์ค่าจาก preview ที่คำนวณด้วย input ว่างเสมอ จึงไม่ตรงกับที่จะบันทึก) */}
      {letterType === 'salary_cert' && overridePreview && (
        <div className="flex gap-2 py-1 border-t border-gray-200 mt-1 pt-2">
          <div className="w-40 shrink-0 text-gray-500">ยอดที่จะรับรองจริง</div>
          <div className="flex-1 font-medium text-brand-700">{overridePreview}</div>
        </div>
      )}
      {registry.nationalIdMasked && row('เลขประจำตัวประชาชน', registry.nationalIdMasked)}

      {needRegistryFix.length > 0 && (
        <div className="mt-3 flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-amber-900">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">ทะเบียนยังไม่ครบ ออกเอกสารไม่ได้</div>
            <div className="text-xs mt-0.5">
              ขาด: {needRegistryFix.map((m) => m.label).join(' · ')} — แก้ที่ทะเบียนพนักงานแล้วกลับมาดึงข้อมูลใหม่
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function HRLetterForm({ mode }: { mode: 'create' | 'edit' }) {
  const navigate = useNavigate();
  const toast = useToast();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const letterId = mode === 'edit' ? params.id : undefined;

  const existing = useLetter(letterId);
  const create = useCreateLetter();
  const patch = usePatchLetter();
  const { data: empsRaw, isLoading: empsLoading, error: empsError, refetch: refetchEmps } = useEmployees();

  const employees = useMemo<EmpOption[]>(() => {
    const raw = empsRaw as { employees?: EmpOption[] } | EmpOption[] | undefined;
    return Array.isArray(raw) ? raw : (raw?.employees ?? []);
  }, [empsRaw]);

  const requestedType = searchParams.get('type');
  const [letterType, setLetterType] = useState<LetterType>(
    mode === 'create' && requestedType === 'warning' ? 'warning' : 'employment_cert',
  );
  const [empCode, setEmpCode] = useState(mode === 'create' ? (searchParams.get('emp') ?? '') : '');
  // fb#261: พนักงานเยอะ เลื่อนหาใน dropdown ไม่ไหว → พิมพ์ชื่อ/ชื่อเล่น/รหัส/แผนกกรองรายการ
  const [empQuery, setEmpQuery] = useState('');
  const shownEmployees = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => e.emp_code === empCode || empMatches(e, q));
  }, [employees, empQuery, empCode]);
  const [purpose, setPurpose] = useState('');
  const [overrideOn, setOverrideOn] = useState(false);
  const [overrideAmount, setOverrideAmount] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [allowanceLabel, setAllowanceLabel] = useState('');
  const [allowanceAmount, setAllowanceAmount] = useState('');
  const [warningLevel, setWarningLevel] = useState<LetterWarningLevel>('first');
  const [warningCategory, setWarningCategory] = useState<LetterWarningCategory | ''>('');
  const [warningCategoryOther, setWarningCategoryOther] = useState('');
  const [incidentDate, setIncidentDate] = useState('');
  const [incidentDetail, setIncidentDetail] = useState('');
  const [ruleRef, setRuleRef] = useState('');
  const [priorActions, setPriorActions] = useState('');
  const [requiredAction, setRequiredAction] = useState('');
  const [witnessName, setWitnessName] = useState('');
  // fb#645 ผู้ลงนามแทนเฉพาะฉบับ — ไม่แตะผู้มีอำนาจลงนามในตั้งค่าบริษัท
  const [signerOn, setSignerOn] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 10);

  // โหมดแก้ร่าง — เติมค่าที่เคยกรอกกลับเข้าฟอร์ม
  useEffect(() => {
    const row = existing.data;
    if (mode !== 'edit' || !row) return;
    setLetterType(row.letter_type);
    setEmpCode(row.emp_code);
    setPurpose(row.input.purpose ?? '');
    setOverrideOn(!!row.input.salaryOverride);
    setOverrideAmount(row.input.salaryOverride ? String(row.input.salaryOverride.amount) : '');
    setOverrideReason(row.input.salaryOverride?.reason ?? '');
    setAllowanceLabel(row.input.allowance?.label ?? '');
    setAllowanceAmount(row.input.allowance ? String(row.input.allowance.amount) : '');
    setWarningLevel(row.input.warning?.level ?? 'first');
    setWarningCategory(row.input.warning?.category ?? '');
    setWarningCategoryOther(row.input.warning?.categoryOther ?? '');
    setIncidentDate(row.input.warning?.incidentDate ?? '');
    setIncidentDetail(row.input.warning?.incidentDetail ?? '');
    setRuleRef(row.input.warning?.ruleRef ?? '');
    setPriorActions(row.input.warning?.priorActions ?? '');
    setRequiredAction(row.input.warning?.requiredAction ?? '');
    setWitnessName(row.input.warning?.witnessName ?? '');
    setSignerOn(!!row.input.signer);
    setSignerName(row.input.signer?.name ?? '');
    setSignerTitle(row.input.signer?.title ?? '');
  }, [mode, existing.data]);

  const warningInput = useMemo<WarningInput>(() => {
    const out: WarningInput = { level: warningLevel };
    if (warningCategory) out.category = warningCategory;
    if (warningCategory === 'other' && warningCategoryOther.trim()) {
      out.categoryOther = warningCategoryOther.trim();
    }
    if (incidentDate) out.incidentDate = incidentDate;
    if (incidentDetail.trim()) out.incidentDetail = incidentDetail.trim();
    if (ruleRef.trim()) out.ruleRef = ruleRef.trim();
    if (priorActions.trim()) out.priorActions = priorActions.trim();
    if (requiredAction.trim()) out.requiredAction = requiredAction.trim();
    if (witnessName.trim()) out.witnessName = witnessName.trim();
    return out;
  }, [
    warningLevel,
    warningCategory,
    warningCategoryOther,
    incidentDate,
    incidentDetail,
    ruleRef,
    priorActions,
    requiredAction,
    witnessName,
  ]);

  /**
   * ⭐ ส่งสิ่งที่กรอกไปให้พรีวิวคิดด้วย ไม่งั้นแถบ "ยังกรอกไม่ครบ" ขึ้นว่าไม่มีค่าจ้างในทะเบียน
   * ทั้งที่ HR ระบุยอดเองไว้แล้ว — จอบอกว่าออกไม่ได้ทั้งที่เซิร์ฟเวอร์ยอมออก
   * ใส่เฉพาะชุดที่ครบจริง (มียอด + มีเหตุผล) เพราะกลางคันเซิร์ฟเวอร์ตอบ 400 ตามด่านของมัน
   */
  const previewInput = useMemo(() => {
    if (letterType === 'warning') return { warning: warningInput };
    const out: Record<string, unknown> = {};
    if (signerOn && signerName.trim() && signerTitle.trim()) {
      out.signer = { name: signerName.trim(), title: signerTitle.trim() };
    }
    if (letterType !== 'salary_cert') return out;
    const amt = Number(overrideAmount.replace(/,/g, '').trim());
    if (overrideOn && Number.isFinite(amt) && amt > 0 && overrideReason.trim()) {
      out.salaryOverride = { amount: amt, reason: overrideReason.trim() };
    }
    const aAmt = Number(allowanceAmount.replace(/,/g, '').trim());
    if (Number.isFinite(aAmt) && aAmt > 0 && allowanceLabel.trim()) {
      out.allowance = { label: allowanceLabel.trim(), amount: aAmt };
    }
    return out;
  }, [
    letterType,
    overrideOn,
    overrideAmount,
    overrideReason,
    allowanceLabel,
    allowanceAmount,
    warningInput,
    signerOn,
    signerName,
    signerTitle,
  ]);

  // หน่วงไว้ก่อนยิง — ไม่งั้นพิมพ์ยอด 1 ตัวอักษร = ยิงเซิร์ฟเวอร์ 1 ครั้ง
  const [debouncedPreview, setDebouncedPreview] = useState<{
    letterType: LetterType;
    input: Record<string, unknown>;
  }>({ letterType, input: {} });
  useEffect(() => {
    const t = setTimeout(() => setDebouncedPreview({ letterType, input: previewInput }), 400);
    return () => clearTimeout(t);
  }, [letterType, previewInput]);

  const debouncedInput = debouncedPreview.letterType === letterType ? debouncedPreview.input : {};

  // ดูผลจริงจาก server ก่อนบันทึก — โหมดแก้ร่างคิดบน snapshot ที่บันทึกไว้ (ส่ง draft id)
  const preview = useLetterPreview(
    mode === 'create'
      ? { emp: empCode, type: letterType, input: debouncedInput }
      : { draftId: letterId, input: debouncedInput },
  );

  /**
   * ทะเบียนต้องมาจากร่างที่บันทึกไว้เสมอในโหมดแก้ — อัปเดตทะเบียนทำผ่านปุ่ม "ดึงข้อมูลใหม่"
   * ที่ให้ HR อ่านก่อน ไม่ใช่เปลี่ยนใต้มือขณะพิมพ์
   * 🚨 ส่วน "ออกได้ไหม" ต้องมาจากพรีวิวที่คิดด้วยสิ่งที่กำลังกรอก — เดิมโหมดแก้ใช้ค่าที่
   * บันทึกไว้ล่าสุด ร่างที่ไม่มีฐานค่าจ้างจึงขึ้นว่าออกไม่ได้ ทั้งที่กรอกยอดระบุเองครบแล้ว
   */
  const registry = mode === 'edit' ? existing.data?.registry : preview.data?.registry;
  const computed = preview.data?.computed ?? (mode === 'edit' ? existing.data?.computed : undefined);
  const missing = (preview.data?.missing ?? (mode === 'edit' ? existing.data?.missing : [])) ?? [];
  const isOwner = useAuthStore((s) => s.profile?.role) === 'OWNER';
  const { data: caps = [], isLoading: capsLoading } = useMyCapabilities();
  // หนังสือเตือน: worker ต้องการ edit_employee นอกจาก view_basic — จอต้องไม่โชว์ตัวเลือกที่กดแล้วโดน 403
  const canWarn = isOwner || caps.includes('edit_employee');
  useEffect(() => {
    if (mode === 'create' && letterType === 'warning' && !capsLoading && !canWarn) setLetterType('employment_cert');
  }, [mode, letterType, capsLoading, canWarn]);
  const meta = TYPES.find((t) => t.value === letterType)!;
  /**
   * 🚨 ห้าม fallback เป็น "เดือน" — พนักงานรายวันที่ยังไม่ตั้งเรทจะไม่มี `wage`
   * แล้วป้ายขึ้น "บาท/เดือน" ทั้งที่ backend ตีความยอดเป็นบาท/วันตามชนิดการจ้าง
   * → กรอก 15,000 ตามป้าย เอกสารรับรอง 15,000 บาท/วัน (บั๊กเดิม เข้าทางอื่น)
   * หน่วยต้องมาจากชนิดการจ้างเสมอ ซึ่งมีใน registry ทุกกรณี
   */
  const unitLabel = registry
    ? WAGE_UNIT_LABEL[
        registry.wage?.unit ??
          (registry.employeeType === 'daily' ? 'day' : registry.employeeType === 'hourly' ? 'hour' : 'month')
      ]
    : '';
  const overrideNum = Number(overrideAmount.replace(/,/g, '').trim());
  const overridePreview =
    overrideOn && Number.isFinite(overrideNum) && overrideNum > 0
      ? `${overrideNum.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท/${unitLabel} (ระบุเอง)`
      : undefined;

  /** ตัวเลขที่กรอกต้องเป็นตัวเลขจริง — ห้ามปล่อย NaN ไปให้ JSON แปลงเป็น null เงียบ ๆ */
  function num(raw: string, label: string): number | undefined {
    const t = raw.replace(/,/g, '').trim();
    if (!t) return undefined;
    const n = Number(t);
    if (!Number.isFinite(n)) throw new Error(`${label}ต้องเป็นตัวเลข`);
    if (n < 0) throw new Error(`${label}ติดลบไม่ได้`);
    return n;
  }

  /**
   * ⭐ API แยกชัดว่า **ไม่ส่ง = คงเดิม · ส่ง null = ล้าง**
   * ตอนแก้ร่างจึงต้องส่ง `null` ให้ช่องที่ผู้ใช้ล้าง ไม่ใช่ละทิ้งไป
   * (บั๊กเดิม: เอาติ๊ก "ระบุยอดเอง" ออกแล้วกดบันทึก ระบบขึ้นว่าสำเร็จแต่ยอดเดิมยังอยู่)
   */
  function buildInput(): Record<string, unknown> {
    const input: Record<string, unknown> = {};
    input.purpose = letterType === 'warning' ? null : (purpose.trim() ? purpose.trim() : null);

    if (letterType === 'warning') {
      // warning ใช้ replace semantics — ส่งทั้งก้อนทุกครั้งเพื่อให้ช่องที่ล้างไม่ค้างจากรอบก่อน
      input.warning = warningInput;
    }

    if (letterType === 'salary_cert') {
      if (overrideOn) {
        const amount = num(overrideAmount, 'ยอดค่าจ้างที่ระบุเอง');
        if (amount == null) throw new Error('กรอกยอดค่าจ้างที่ต้องการรับรอง');
        if (amount <= 0) throw new Error('ยอดค่าจ้างที่ระบุเองต้องมากกว่า 0');
        if (!overrideReason.trim()) throw new Error('ระบุยอดเองต้องใส่เหตุผลกำกับด้วย');
        input.salaryOverride = { amount, reason: overrideReason.trim() };
      } else {
        input.salaryOverride = null;
      }

      /**
       * 🚨 เดิมจอแปลงคู่ที่กรอกไม่ครบเป็น null ให้เอง = ส่งไปเป็น "ล้างค่า"
       * ด่านฝั่งเซิร์ฟเวอร์ที่เพิ่งเพิ่มจึงไม่มีวันเห็น ผู้ใช้ยังได้ข้อความ "บันทึกสำเร็จ"
       * ทั้งที่ค่าตำแหน่ง 3,000 ที่เพิ่งพิมพ์หายไปจากเอกสาร
       */
      const aAmount = num(allowanceAmount, 'เงินได้ประจำอื่น');
      const aLabel = allowanceLabel.trim();
      if (aAmount != null && !aLabel) {
        throw new Error('ใส่ชื่อรายการของเงินได้ประจำอื่นด้วย (เช่น ค่าตำแหน่ง) — ไม่งั้นยอดนี้จะไม่ขึ้นบนเอกสาร');
      }
      if (aLabel && aAmount == null) {
        throw new Error('ใส่ยอดเงินของรายการนี้ด้วย หรือถ้าไม่ต้องการแล้วให้ลบชื่อรายการออก');
      }
      input.allowance = aAmount != null && aLabel ? { label: aLabel, amount: aAmount } : null;
    }

    if (letterType !== 'warning') {
      if (signerOn) {
        if (!signerName.trim() || !signerTitle.trim()) {
          throw new Error('ผู้ลงนามแทนต้องใส่ทั้งชื่อ-สกุลและตำแหน่ง');
        }
        input.signer = { name: signerName.trim(), title: signerTitle.trim() };
      } else {
        input.signer = null;
      }
    }
    return input;
  }

  async function submit() {
    try {
      if (!empCode) throw new Error('กรุณาเลือกพนักงาน');
      const input = buildInput();
      if (mode === 'create') {
        const row = await create.mutateAsync({ emp_code: empCode, letter_type: letterType, input });
        toast.success('บันทึกร่างแล้ว — ตรวจแล้วกดออกจริงที่หน้ารายการ');
        navigate(`/hr/letters?highlight=${row.id}`);
      } else {
        const row = existing.data!;
        await patch.mutateAsync({ id: row.id, expected_revision: row.revision, input });
        toast.success('บันทึกการแก้ไขแล้ว');
        navigate('/hr/letters');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  if (mode === 'edit' && existing.isLoading) {
    return (
      <AdminLayout title="แก้ร่างหนังสือ">
        <Loading />
      </AdminLayout>
    );
  }
  if (mode === 'edit' && existing.error) {
    return (
      <AdminLayout title="แก้ร่างหนังสือ">
        <LoadError error={existing.error} onRetry={existing.refetch} />
      </AdminLayout>
    );
  }
  if (mode === 'edit' && existing.data && existing.data.status !== 'draft') {
    return (
      <AdminLayout title="แก้ร่างหนังสือ">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
          หนังสือฉบับนี้ออกไปแล้วหรือถูกยกเลิกแล้ว จึงแก้ไม่ได้ — ถ้าต้องแก้เนื้อหา ให้ยกเลิกแล้วออกฉบับใหม่
        </div>
      </AdminLayout>
    );
  }

  const busy = create.isPending || patch.isPending;

  return (
    <AdminLayout title={mode === 'create' ? 'ออกหนังสือ HR' : 'แก้ร่างหนังสือ'}>
      <button
        onClick={() => navigate('/hr/letters')}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> กลับหน้ารายการหนังสือ
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl">
        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-5">
            <FileText className="w-5 h-5 text-brand-600" />
            <h2 className="text-lg font-bold">{mode === 'create' ? 'ออกหนังสือใหม่' : 'แก้ร่าง'}</h2>
          </div>

          <div className="mb-5">
            <label className={lbl}>ชนิดหนังสือ</label>
            <select
              className={inp}
              value={letterType}
              disabled={mode === 'edit'}
              onChange={(e) => setLetterType(e.target.value as LetterType)}
            >
              {TYPES.filter((t) => t.value !== 'warning' || canWarn).map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1.5">{meta.hint}</p>
          </div>

          <div className="mb-5">
            <label className={lbl}>พนักงาน</label>
            {mode === 'create' && (
              <input
                className={`${inp} mb-2`}
                type="search"
                placeholder="พิมพ์ชื่อ ชื่อเล่น รหัส หรือแผนก เพื่อค้นหา"
                value={empQuery}
                disabled={empsLoading}
                onChange={(e) => {
                  const q = e.target.value;
                  setEmpQuery(q);
                  // พิมพ์จนเหลือคนเดียว → เลือกให้เลย ไม่ต้องกดซ้ำ
                  const t = q.trim().toLowerCase();
                  if (!t) return;
                  const hits = employees.filter((x) => empMatches(x, t));
                  if (hits.length === 1) setEmpCode(hits[0]!.emp_code);
                }}
              />
            )}
            <select
              className={inp}
              value={empCode}
              disabled={mode === 'edit' || empsLoading}
              onChange={(e) => setEmpCode(e.target.value)}
            >
              <option value="">
                {empsLoading
                  ? 'กำลังโหลดรายชื่อ…'
                  : empQuery.trim()
                    ? `— พบ ${shownEmployees.filter((e) => empMatches(e, empQuery.trim().toLowerCase())).length} คน เลือกจากรายการ —`
                    : '— เลือกพนักงาน —'}
              </option>
              {shownEmployees.map((e) => (
                <option key={e.emp_code} value={e.emp_code}>
                  {e.emp_code} · {`${e.first_name ?? ''} ${e.last_name ?? ''}`.trim()}
                  {e.nickname ? ` (${e.nickname})` : ''}
                  {e.position ? ` — ${e.position}` : ''}
                </option>
              ))}
            </select>
            {mode === 'edit' && (
              <p className="text-xs text-gray-500 mt-1.5">เปลี่ยนพนักงานหรือชนิดหนังสือไม่ได้ ต้องออกฉบับใหม่</p>
            )}
            {/* โหลดรายชื่อล้มแล้วปล่อยช่องว่างเปล่า = ผู้ใช้ไม่รู้ว่าเกิดอะไรขึ้นและทำอะไรต่อไม่ได้ */}
            {empsError && mode === 'create' && (
              <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2.5 text-sm text-red-800">
                โหลดรายชื่อพนักงานไม่สำเร็จ{' '}
                <button onClick={() => void refetchEmps()} className="underline font-medium">
                  ลองใหม่
                </button>
              </div>
            )}
          </div>

          {letterType === 'warning' && (
            <fieldset className="mb-5 rounded-xl border border-amber-200 bg-amber-50/40 p-4 space-y-4">
              <legend className="px-1 text-sm font-semibold text-gray-800">รายละเอียดหนังสือเตือน</legend>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>ครั้งที่เตือน</label>
                  <select
                    className={inp}
                    value={warningLevel}
                    onChange={(e) => setWarningLevel(e.target.value as LetterWarningLevel)}
                    required
                  >
                    {(Object.entries(WARNING_LEVEL_LABEL) as Array<[LetterWarningLevel, string]>).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={lbl}>ประเภทความผิด</label>
                  <select
                    className={inp}
                    value={warningCategory}
                    onChange={(e) => setWarningCategory(e.target.value as LetterWarningCategory | '')}
                    required
                  >
                    <option value="">— เลือกประเภทความผิด —</option>
                    {(Object.entries(WARNING_CATEGORY_LABEL) as Array<[LetterWarningCategory, string]>).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {warningCategory === 'other' && (
                <div>
                  <label className={lbl}>ระบุ</label>
                  <input
                    className={inp}
                    value={warningCategoryOther}
                    onChange={(e) => setWarningCategoryOther(e.target.value)}
                    required
                  />
                </div>
              )}

              <div>
                <label className={lbl}>วันที่กระทำผิด</label>
                <input
                  type="date"
                  className={`input ${inp}`}
                  value={incidentDate}
                  max={today}
                  onChange={(e) => setIncidentDate(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className={lbl}>รายละเอียดเหตุการณ์</label>
                <textarea
                  className={inp}
                  rows={4}
                  value={incidentDetail}
                  onChange={(e) => setIncidentDetail(e.target.value)}
                  placeholder="ทำอะไร เมื่อไร ที่ไหน มีหลักฐานอะไร เช่น สแกนเข้างาน 09:47 น. วันที่ 15 ก.ย. 2569 ตามบันทึกระบบสแกน"
                  required
                />
              </div>

              <div>
                <label className={lbl}>ข้อบังคับ/ระเบียบที่ฝ่าฝืน (ไม่บังคับ)</label>
                <textarea
                  className={inp}
                  rows={2}
                  value={ruleRef}
                  onChange={(e) => setRuleRef(e.target.value)}
                  placeholder="เช่น ข้อบังคับเกี่ยวกับการทำงาน หมวด 3 ข้อ 3.1 เวลาทำงานปกติ"
                />
              </div>

              <div>
                <label className={lbl}>การตักเตือนก่อนหน้า (ไม่บังคับ)</label>
                <textarea
                  className={inp}
                  rows={2}
                  value={priorActions}
                  onChange={(e) => setPriorActions(e.target.value)}
                />
              </div>

              <div>
                <label className={lbl}>สิ่งที่ต้องปรับปรุง (ไม่บังคับ · ว่างไว้ = ไม่พิมพ์หัวข้อนี้)</label>
                <textarea
                  className={inp}
                  rows={3}
                  value={requiredAction}
                  onChange={(e) => setRequiredAction(e.target.value)}
                />
              </div>

              <div>
                <label className={lbl}>ชื่อพยาน (ไม่บังคับ)</label>
                <input
                  className={inp}
                  value={witnessName}
                  onChange={(e) => setWitnessName(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1.5">ช่องลงนามพยานพิมพ์บนเอกสารเสมอ · ไม่ใส่ชื่อ = เว้นเส้นว่างให้เขียนเอง</p>
              </div>
            </fieldset>
          )}

          {letterType !== 'warning' && (
            <div className="mb-5">
              <label className={lbl}>ออกให้เพื่อ (ไม่บังคับ)</label>
              <input
                className={inp}
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="เช่น ใช้ประกอบการยื่นขอสินเชื่อกับธนาคาร"
              />
            </div>
          )}

          {letterType === 'salary_cert' && (
            <div className="border-t border-gray-100 pt-5 space-y-4">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="w-4 h-4 mt-0.5"
                  checked={overrideOn}
                  onChange={(e) => setOverrideOn(e.target.checked)}
                />
                <span className="text-sm">
                  ระบุยอดค่าจ้างเอง แทนฐานในระบบ
                  <span className="block text-xs text-gray-500">
                    ปกติไม่ต้องติ๊ก — ระบบใช้ฐานค่าจ้างจากทะเบียนให้อยู่แล้ว
                  </span>
                </span>
              </label>

              {overrideOn && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-3">
                  <div>
                    <label className={lbl}>ยอดที่ต้องการรับรอง (บาท/{unitLabel})</label>
                    <input
                      className={inp}
                      inputMode="decimal"
                      value={overrideAmount}
                      onChange={(e) => setOverrideAmount(e.target.value)}
                    />
                    <p className="text-xs text-amber-800 mt-1">
                      หน่วยยึดตามชนิดการจ้างของพนักงานคนนี้ ({unitLabel}) เปลี่ยนเองไม่ได้
                    </p>
                  </div>
                  <div>
                    <label className={lbl}>เหตุผล (บังคับ)</label>
                    <input
                      className={inp}
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="เช่น รวมค่าตำแหน่งตามสัญญาจ้าง"
                    />
                    <p className="text-xs text-amber-800 mt-1">ข้อความนี้จะถูกพิมพ์เป็นหมายเหตุบนเอกสาร</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>เงินได้ประจำอื่น (ไม่บังคับ)</label>
                  <input
                    className={inp}
                    value={allowanceLabel}
                    onChange={(e) => setAllowanceLabel(e.target.value)}
                    placeholder="เช่น ค่าตำแหน่ง"
                  />
                </div>
                <div>
                  <label className={lbl}>จำนวน (บาท)</label>
                  <input
                    className={inp}
                    inputMode="decimal"
                    value={allowanceAmount}
                    onChange={(e) => setAllowanceAmount(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {letterType !== 'warning' && (
            <fieldset className="border-t border-gray-100 pt-5 mt-5 space-y-3">
              <legend className={lbl}>ผู้ลงนามในหนังสือฉบับนี้</legend>
              <label className="flex items-start gap-2 min-h-[44px]">
                <input
                  type="radio"
                  name="signer"
                  className="w-4 h-4 mt-1"
                  checked={!signerOn}
                  onChange={() => setSignerOn(false)}
                />
                <span className="text-sm">
                  ผู้มีอำนาจลงนามตามตั้งค่าบริษัท
                  <span className="block text-xs text-gray-500">
                    {registry?.company.signerName
                      ? `${registry.company.signerName}${registry.company.signerTitle ? ` · ${registry.company.signerTitle}` : ''}`
                      : 'ยังไม่ได้ตั้งชื่อในหน้าตั้งค่าบริษัท'}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 min-h-[44px]">
                <input
                  type="radio"
                  name="signer"
                  className="w-4 h-4 mt-1"
                  checked={signerOn}
                  onChange={() => setSignerOn(true)}
                />
                <span className="text-sm">
                  ลงนามแทน (เช่น HR หรือเลขานุการ)
                  <span className="block text-xs text-gray-500">
                    ใช้เฉพาะฉบับนี้ ไม่เปลี่ยนชื่อกรรมการในตั้งค่าบริษัท
                  </span>
                </span>
              </label>
              {signerOn && (
                <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-3">
                  <div>
                    <label className={lbl}>เลือกจากรายชื่อพนักงาน (ไม่บังคับ)</label>
                    <select
                      className={inp}
                      value=""
                      onChange={(e) => {
                        const p = employees.find((x) => x.emp_code === e.target.value);
                        if (!p) return;
                        setSignerName(`${p.first_name ?? ''} ${p.last_name ?? ''}`.trim());
                        setSignerTitle(p.position ?? '');
                      }}
                    >
                      <option value="">— เลือกเพื่อเติมชื่อและตำแหน่งให้ —</option>
                      {employees.map((e) => (
                        <option key={e.emp_code} value={e.emp_code}>
                          {`${e.first_name ?? ''} ${e.last_name ?? ''}`.trim()}
                          {e.position ? ` — ${e.position}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={lbl}>ชื่อ-สกุลผู้ลงนามแทน</label>
                      <input className={inp} value={signerName} onChange={(e) => setSignerName(e.target.value)} />
                    </div>
                    <div>
                      <label className={lbl}>ตำแหน่ง</label>
                      <input
                        className={inp}
                        value={signerTitle}
                        onChange={(e) => setSignerTitle(e.target.value)}
                        placeholder="เช่น ผู้จัดการฝ่ายบุคคล"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    เอกสารพิมพ์ชื่อนี้ใต้คำว่า “ผู้ลงนามแทนผู้มีอำนาจลงนาม” และเว้นเส้นให้เซ็นมือ
                    (ไม่ใช้รูปลายมือชื่อกรรมการ)
                  </p>
                </div>
              )}
            </fieldset>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-5 mt-5">
            <button
              onClick={() => navigate('/hr/letters')}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              ยกเลิก
            </button>
            <button
              onClick={submit}
              disabled={busy || !empCode}
              className="px-4 py-2 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? 'กำลังบันทึก…' : mode === 'create' ? 'บันทึกร่าง' : 'บันทึกการแก้ไข'}
            </button>
          </div>
        </div>

        <div>
          {mode === 'create' && !empCode && (
            <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              เลือกพนักงานเพื่อดูข้อมูลที่จะขึ้นบนเอกสาร
            </div>
          )}
          {mode === 'create' && empCode && preview.isLoading && <Loading />}
          {/* โหมดแก้ร่างถ้าพรีวิวล้มแล้วเงียบ จอจะถอยไปโชว์ผลที่บันทึกไว้ = บอกว่า
              "ขาดฐานค่าจ้าง" ทั้งที่ HR กรอก override ครบแล้ว โดยไม่มีทางรู้ว่าเกิดอะไรขึ้น */}
          {(mode === 'edit' || empCode) && preview.error && (
            <LoadError error={preview.error} onRetry={preview.refetch} />
          )}
          {registry && computed && (
            <RegistryPanel
              registry={registry}
              computed={computed}
              missing={missing}
              letterType={letterType}
              overridePreview={overridePreview}
              isOwner={isOwner}
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
